<template>
    <div>
      <slot
        :results="results"
        :totalResults="totalResults"
        :hasNextPage="hasNextPage"
        :hasPreviousPage="hasPreviousPage"
        :switchPage="switchPage"
        :tabs="tabs"
        :loading="loading"
        :upcomingSlider="upcomingSlider"
        :eventsTabLink="eventsTabLink"
    ></slot>
    </div>
  </template>

  <script>
import { defineComponent, onMounted, ref } from 'vue';
import zonedDate from '../utils/zoned-date';
import { format } from 'date-fns';

const now = format(zonedDate(), 'yyyy-MM-dd');

export default defineComponent({
    name: 'SearchResults',
    setup() {
        const queryParam = ref('q');
        const query = ref('');
        const results = ref([]);
        const allResults = ref([]);
        const hasNextPage = ref(false);
        const hasPreviousPage = ref(false);
        const currentPage = ref(1);
        const totalResults = ref(0);
        const filterParam = ref('filter');
        const tabs = ref([]);
        const loading = ref(true);
        const upcomingSlider = ref([]);
        const eventsTabLink = ref(null);

        const handleUpcomingSearchResults = ((items) => {
            upcomingSlider.value = items.filter(item => item.collectionType === 'events' && item.date > now);
        });

        const processResults = ((items) => {
            results.value = items
                .filter(item => item.collectionType !== 'people')
                .map(item => {
                const data = {
                    id: item.id,
                    title: item.title,
                    collectionType: item.collectionType,
                    url: item.url,
                    description: item.description,
                    image: item.image ? item.image : null,
                    date: item.date ? format(zonedDate(item.date), 'yyyy-MM-dd') : null,
                };

                if (item.image) {
                    data.image.alt = item.image.alt ? item.image.alt : item.title;
                }

                return data;
            });

            allResults.value = results.value;
            handleUpcomingSearchResults(results.value);
        });

        const getResults = ((url) => {
            // Use below to use LIVE search results
            // url = url.replace('http://127.0.0.1:5555/', 'https://www.writerstheatre.org/');

            fetch(url)
                .then(response => response.json())
                .then(response => {
                    processResults(response.docs);
                    totalResults.value = results.value.length;
                    hasNextPage.value = response.hasNextPage;
                    hasPreviousPage.value = response.hasPrevPage;
                    generateTabs();
                    applyFiltering();
                })
                .finally(() => {
                    loading.value = false;
                });
        });

        const applyFiltering = (() => {
            const search = new URLSearchParams(window.location.search);
            const filter = search.has(filterParam.value) ? search.get(filterParam.value) : 'all';

            if (filter === 'all') {
                results.value = allResults.value;
            } else {
                results.value = allResults.value.filter(item => item.collectionType === filter);
            }
        });

        const getTotalResultsForTab = ((filter) => {
            return allResults.value.filter(item => item.collectionType === filter).length;
        });

        const doSearch = (() => {
            // TODO: This probably needs changing to use the search API
            const url = new URL('/api/search', window.location.origin);

            url.searchParams.set('locale', 'en');
            url.searchParams.set('depth', '2');
            url.searchParams.set('page', currentPage.value);
            url.searchParams.set('q', query.value);

            getResults(url.toString());
        });

        const switchPage = ((step) => {
            currentPage.value = parseInt(currentPage.value) + parseInt(step);
            doSearch();
        });

        onMounted(() => {
            const search = new URLSearchParams(window.location.search);
            query.value = search.has(queryParam.value) ? search.get(queryParam.value) : '';
            currentPage.value = search.has('page') ? search.get('page') : 1;

            doSearch();
        });

        const searchFilterURL = ((filter, page) => {
            const url = new URL(window.location);

            url.searchParams.set(filterParam.value, filter);

            if (page) {
                url.searchParams.set('p', page);
            }

            return url.toString();
        });

        const generateTabs = (() => {
            const search = new URLSearchParams(window.location.search);

            const filterTabs = [
                {
                    filter: 'all',
                    name: 'All',
                    link: searchFilterURL('all', 1),
                    isActive: !search.has(filterParam.value) || search.get(filterParam.value) === 'all',
                    totalResults: totalResults.value,
                },
                {
                    filter: 'events',
                    name: 'Events',
                    link: eventsTabLink.value = searchFilterURL('events', 1),
                    isActive: search.has(filterParam.value) && search.get(filterParam.value) === 'events',
                    totalResults: getTotalResultsForTab('events'),
                },
                {
                    filter: 'posts',
                    name: 'News',
                    link: searchFilterURL('posts', 1),
                    isActive: search.has(filterParam.value) && search.get(filterParam.value) === 'posts',
                    totalResults: getTotalResultsForTab('posts'),
                },
            ];

            tabs.value = filterTabs;
        });

        return {
            results,
            totalResults,
            hasNextPage,
            hasPreviousPage,
            switchPage,
            tabs,
            loading,
            upcomingSlider,
            eventsTabLink,
        };
    }
});
</script>
